import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Pagination } from '../../components/ui/pagination';
import {
  Plus,
  Folder,
  Download,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  FileText,
  Calendar,
  User,
} from 'lucide-react';
import { Document } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { getFileIcon } from './utils';
import { CanDeleteDocument, showsDelete } from './documentPermissions';

interface ListViewProps {
  documents: Document[];
  searchTerm: string;
  onDownload: (doc: Document) => void;
  onUploadOpen: () => void;
  onDelete?: (doc: Document) => void;
  /** CR-8681: per-document Delete check; absent offers Delete on every row. */
  canDelete?: CanDeleteDocument;
  onEdit?: (doc: Document) => void;
  onPreview?: (doc: Document) => void;
  // CR-5330: Open the document details popup. When provided, clicking
  // a row body opens the modal; the inline Download button and kebab
  // menu stop propagation so they remain quick actions.
  onOpenDetail?: (doc: Document) => void;
  currentPage?: number;
  totalPages?: number;
  totalCount?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

export function ListView({
  documents,
  searchTerm,
  onDownload,
  onUploadOpen,
  onDelete,
  canDelete,
  onEdit,
  onPreview,
  onOpenDetail,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: ListViewProps) {
  const { formatDate } = useFormatters();

  if (documents.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            {searchTerm ? 'No documents found matching your search' : 'No documents yet'}
          </p>
          <Button className="mt-4" onClick={onUploadOpen}>
            <Plus className="h-4 w-4 mr-2" />
            Upload your first document
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Render top + bottom pagination controls whenever we have more than one
  // page. The parent owns the pagination state so the URL query + backend
  // calls stay authoritative.
  const pager = (currentPage && totalPages && totalPages > 1 && onPageChange) ? (
    <Pagination
      currentPage={currentPage}
      totalPages={totalPages}
      totalCount={totalCount || 0}
      itemsPerPage={pageSize || 20}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      itemLabel="documents"
    />
  ) : null;

  return (
    <div className="space-y-3">
      {pager}
      <Card>
        <CardContent className="p-0">
          <div className="divide-y">
          {documents.map((doc) => {
            // CR-5330: Row body opens the details popup. Inline action
            // controls stopPropagation so a button click doesn't also
            // fire the row click.
            const handleRowClick = () => onOpenDetail?.(doc);
            const stop = (e: React.MouseEvent) => e.stopPropagation();
            return (
            <div
              key={doc.id}
              className={`p-4 hover:bg-muted/50 transition-colors flex items-center justify-between gap-4 ${onOpenDetail ? 'cursor-pointer' : ''}`}
              onClick={onOpenDetail ? handleRowClick : undefined}
              role={onOpenDetail ? 'button' : undefined}
              tabIndex={onOpenDetail ? 0 : undefined}
              onKeyDown={onOpenDetail ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenDetail(doc); } } : undefined}
            >
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="text-muted-foreground">
                  {getFileIcon(doc)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold truncate">{doc.title}</h3>
                    {doc.is_public && (
                      <Badge variant="outline" className="text-xs">
                        Public
                      </Badge>
                    )}
                    {doc.requires_acceptance && (
                      <Badge className="text-xs bg-amber-100 text-amber-900 hover:bg-amber-100 border-amber-200">
                        Requires acceptance
                      </Badge>
                    )}
                  </div>
                  {doc.description && (
                    <p className="text-sm text-muted-foreground truncate mt-1">
                      {doc.description}
                    </p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2">
                    {doc.category_name && (
                      <div className="flex items-center gap-1">
                        <Folder className="h-3 w-3" />
                        <span>{doc.category_name}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      <span>{doc.uploaded_by_name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>{formatDate(doc.version_uploaded_at || doc.uploaded_at)}</span>
                    </div>
                    {doc.formatted_file_size && <span>{doc.formatted_file_size}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2" onClick={stop}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDownload(doc)}
                >
                  <Download className="h-4 w-4" />
                </Button>
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onPreview?.(doc)}>
                      <Eye className="h-4 w-4 mr-2" />
                      Preview
                    </DropdownMenuItem>
                    {onEdit && (
                      <DropdownMenuItem onClick={() => onEdit(doc)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                    )}
                    {onDelete && showsDelete(onDelete, canDelete, doc) && (
                      <DropdownMenuItem className="text-red-600" onClick={() => onDelete(doc)}>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            );
          })}
          </div>
        </CardContent>
      </Card>
      {pager}
    </div>
  );
}
